import { useCallback, useEffect, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import { useRefreshOn } from '../../lib/resourceRefresh';
import type { KbCapacity, KbReadiness } from '../../types/api';
import { fetchKbReadiness } from './knowledgeBaseApi';

export interface KbReadinessResult {
  readiness: KbReadiness | null;
  /**
   * Section counts against the plan's caps, or null while loading, on error,
   * and from a backend that predates the caps (unknown: show nothing).
   */
  capacity: KbCapacity | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

/** Result of the last completed load, tagged with the business it belongs to. */
interface LoadedReadiness {
  businessId: string;
  readiness: KbReadiness | null;
  error: string | null;
}

/**
 * Weighted knowledge-base readiness for one business.
 *
 * Same shape as `useCollection`: the load result is stored tagged with its
 * `business_id` and the visible state is derived during render, so nothing is
 * written to state synchronously inside the effect and a previous business's
 * score can never flash on screen.
 */
export function useKbReadiness(businessId: string | null): KbReadinessResult {
  const [loaded, setLoaded] = useState<LoadedReadiness | null>(null);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((value) => value + 1), []);
  useRefreshOn(businessId, 'kb_readiness', refresh);

  useEffect(() => {
    if (!businessId) return;

    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const readiness = await fetchKbReadiness(businessId, controller.signal);
        if (cancelled) return;
        setLoaded({ businessId, readiness, error: null });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setLoaded({
          businessId,
          readiness: null,
          error: getErrorMessage(caught, 'Could not read your profile strength.'),
        });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [businessId, nonce]);

  const isCurrent = loaded !== null && loaded.businessId === businessId;
  const readiness = isCurrent ? loaded.readiness : null;

  return {
    readiness,
    capacity: readiness?.capacity ?? null,
    isLoading: businessId !== null && !isCurrent,
    error: isCurrent ? loaded.error : null,
    refresh,
  };
}
